import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Quiz Data
const QUESTIONS = {
  love: {
    headline: "In love and relationships...",
    instruction: "Select the statement that feels most true right now.",
    options: [
      { letter: "A", text: "I know how to hold space for everyone else. Being truly held feels surprisingly uncomfortable." },
      { letter: "B", text: "I understand connection deeply. Personally, I still feel like I have nowhere to put what I carry." },
      { letter: "C", text: "I have done the inner work. And love still somehow feels like something I have to earn." },
      { letter: "D", text: "I have almost stopped hoping. But something quiet in me still believes real connection must be possible." },
      { letter: "E", text: "I have built a beautiful life around other people. Somewhere along the way I stopped knowing what I actually need." },
      { letter: "F", text: "I connect easily on the surface. Real intimacy — the kind where I don't have to manage how I'm perceived — still feels out of reach." }
    ]
  },
  health: {
    headline: "When it comes to your body and your health...",
    instruction: "Select the statement that feels most true right now.",
    options: [
      { letter: "A", text: "I know what to do. I teach it or have lived it. My own body still isn't cooperating." },
      { letter: "B", text: "I spend my energy helping others regulate. My own nervous system is running on empty." },
      { letter: "C", text: "I have tried the protocols. Done the cleanses. Seen the practitioners. Something still isn't shifting." },
      { letter: "D", text: "I am so tired of managing this. I just want something to actually reach the level where this lives." },
      { letter: "E", text: "My body has been through a lot. I have made peace with most of it. But something still feels unresolved at a level I can't quite name." },
      { letter: "F", text: "I know the mind and body are connected. I just haven't found anything that actually works at that level consistently." }
    ]
  },
  money: {
    headline: "When you think about money and abundance...",
    instruction: "Select the statement that feels most true right now.",
    options: [
      { letter: "A", text: "I give so much. Receiving — even money — feels strangely uncomfortable." },
      { letter: "B", text: "I help people work through their relationship with money. Mine is still quietly complicated." },
      { letter: "C", text: "I have done the mindset work. I believe in abundance. The ceiling keeps appearing in the same place." },
      { letter: "D", text: "I know scarcity isn't just a money problem. It feels like a frequency I am living from and I don't know how to leave it." },
      { letter: "E", text: "I can create money. Keeping it, growing it, feeling safe with it — that's where something quietly breaks down." },
      { letter: "F", text: "Every time I get close to a breakthrough something pulls me back and I can't fully explain why." }
    ]
  }
};

const SOUL_TYPES = {
  "The Suffering Helper": {
    monologue: "You carry others with extraordinary grace.",
    description: "The gap between what you give and what you allow yourself to receive has been running quietly for a long time. This is not a character flaw. It is a system that made sense once. And it is ready to update."
  },
  "The Undercover Healer": {
    monologue: "You understand the patterns clinically and personally.",
    description: "The hardest thing is that your own system keeps running the same programs you help others clear. This is not hypocrisy. It is what happens when the tools you have only reach the conscious level. Something deeper is available."
  },
  "The Fixer in Disguise": {
    monologue: "You have done the work. More work than most people would attempt.",
    description: "The ceiling you keep hitting is not evidence that something is wrong with you. It is evidence that the pattern lives at a level the work has not yet reached. That level exists."
  },
  "The One on the Brink": {
    monologue: "The fact that you are still here — still willing to answer three questions — is not naivety.",
    description: "It is the most accurate thing about you. The part of you that keeps saying something more must be possible knows something your exhaustion does not."
  },
  "The Quiet Architect": {
    monologue: "You have built something real. Something others can see and admire.",
    description: "But somewhere in the building, you lost track of yourself. The life you created may be beautiful — but it was designed around everyone else's needs. The part of you that knows what you actually want has been waiting patiently. It is still there."
  },
  "The Almost Arrived": {
    monologue: "You have come so close, so many times.",
    description: "The pattern that pulls you back just before the breakthrough is not weakness or self-sabotage in the way you think. It is protection — outdated, but sincere. Something in you learned that arriving was dangerous. That program is ready to be rewritten."
  },
  "The Woven Path": {
    monologue: "Your experience does not fit neatly into one pattern.",
    description: "You carry threads from multiple soul types — each one developed for good reason. This complexity is not confusion. It is depth. What you need is not a single answer but something that can hold all of who you are at once. That exists."
  }
};

// Screen Components
const OpeningScreen = ({ onStart }) => (
  <div className="quiz-screen" data-testid="opening-screen">
    <div className="quiz-content">
      <p
        className="text-sm md:text-base tracking-widest uppercase mb-6 animate-fade-in"
        style={{ color: 'var(--accent)', fontFamily: 'Inter, sans-serif', fontWeight: 500 }}
        data-testid="author-header"
      >
        Mz. Felicia Muhammad
      </p>
      <h1 
        className="text-4xl md:text-5xl lg:text-6xl font-light tracking-tight mb-8 animate-fade-in-up"
        style={{ fontFamily: 'Manrope, sans-serif', color: 'var(--text-primary)' }}
      >
        Something in you already knows.
      </h1>
      <p 
        className="text-lg md:text-xl leading-[1.7] mb-12 animate-fade-in-up animation-delay-200"
        style={{ color: 'var(--text-secondary)' }}
      >
        Answer three questions honestly. We will show you exactly where you are — and what that means.
      </p>
      <button 
        onClick={onStart}
        className="btn-primary animate-fade-in-up animation-delay-400"
        data-testid="start-quiz-btn"
      >
        I'm ready
      </button>
    </div>
  </div>
);

const QuestionScreen = ({ question, selectedAnswer, onSelect, step }) => (
  <div className="quiz-screen" data-testid={`question-screen-${step}`}>
    <div className="quiz-content">
      <h2 
        className="text-2xl md:text-3xl lg:text-4xl font-light tracking-tight mb-4 animate-fade-in-up"
        style={{ fontFamily: 'Manrope, sans-serif', color: 'var(--text-primary)' }}
      >
        {question.headline}
      </h2>
      <p 
        className="text-base md:text-lg mb-10 animate-fade-in-up animation-delay-100"
        style={{ color: 'var(--text-secondary)' }}
      >
        {question.instruction}
      </p>
      <div className="space-y-4">
        {question.options.map((option, index) => (
          <button
            key={option.letter}
            onClick={() => onSelect(option.letter)}
            className={`option-card animate-fade-in-up ${selectedAnswer === option.letter ? 'selected' : ''}`}
            style={{ animationDelay: `${(index + 2) * 0.1}s`, opacity: 0 }}
            data-testid={`option-${option.letter.toLowerCase()}`}
          >
            {option.text}
          </button>
        ))}
      </div>
    </div>
  </div>
);

const RevealScreen = ({ soulType, onContinue }) => {
  // Handle combined types - show unified title "The Woven Path"
  const getDisplayType = () => {
    if (soulType.includes('&') || soulType === 'The Woven Path') {
      return 'The Woven Path';
    }
    return soulType;
  };
  
  const displayType = getDisplayType();
  const typeData = SOUL_TYPES[displayType];
  
  const getTypeInfo = () => {
    return typeData || SOUL_TYPES["The Woven Path"];
  };
  
  const info = getTypeInfo();
  
  return (
    <div className="quiz-screen" data-testid="reveal-screen">
      <div className="reveal-content text-center">
        <p 
          className="soul-type-name animate-fade-in-up"
          data-testid="soul-type-name"
        >
          {displayType}
        </p>
        <p 
          className="reveal-text mb-8 animate-fade-in-up animation-delay-200"
          style={{ fontStyle: 'italic' }}
        >
          {info.monologue}
        </p>
        <p 
          className="reveal-text mb-12 animate-fade-in-up animation-delay-400"
        >
          {info.description}
        </p>
        <button 
          onClick={onContinue}
          className="btn-primary animate-fade-in-up animation-delay-600"
          data-testid="continue-btn"
        >
          Continue
        </button>
      </div>
    </div>
  );
};

const EmailScreen = ({ responseId, onSubmit, onSkip }) => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/email`, { email, response_id: responseId });
      onSubmit();
    } catch (err) {
      toast.error("Could not save your email. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="quiz-screen" data-testid="email-screen">
      <div className="quiz-content">
        <h2 
          className="text-2xl md:text-3xl lg:text-4xl font-light tracking-tight mb-4 animate-fade-in-up"
          style={{ fontFamily: 'Manrope, sans-serif', color: 'var(--text-primary)' }}
        >
          You are seen today.
        </h2>
        <p 
          className="text-base md:text-lg mb-10 animate-fade-in-up animation-delay-100"
          style={{ color: 'var(--text-secondary)' }}
        >
          Something is being built for exactly where you are. Leave your email and we will reach you when it is ready.
        </p>
        <form onSubmit={handleSubmit} className="max-w-md mx-auto">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="email-input mb-8 animate-fade-in-up animation-delay-200"
            data-testid="email-input"
          />
          <div className="flex flex-col gap-4 animate-fade-in-up animation-delay-300">
            <button 
              type="submit"
              disabled={!email || isSubmitting}
              className="btn-primary"
              data-testid="submit-email-btn"
            >
              {isSubmitting ? "Saving..." : "Yes. Reach me."}
            </button>
            <button
              type="button"
              onClick={onSkip}
              className="text-sm"
              style={{ color: 'var(--text-secondary)' }}
              data-testid="skip-email-btn"
            >
              Skip for now
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CLOSING_QUOTES = {
  "The Suffering Helper": "You have carried others with extraordinary grace. Now consider this: The same care you offer so freely — you are worthy of receiving it. That is not weakness. That is wholeness.",
  "The Undercover Healer": "You understand the patterns deeply. What you know has served many. And you — the one doing the knowing — deserve the same level of care. That level exists. And it begins with telling the truth about where you are.",
  "The Fixer in Disguise": "The ceiling you keep meeting is not your limit. It is information. The pattern lives somewhere the work hasn't reached yet. That somewhere is real. And so are you.",
  "The One on the Brink": "The part of you that is still here — still willing — knows something your exhaustion does not. That knowing is not naivety. It is the most accurate thing about you. Trust it.",
  "The Woven Path": "You carry more than one truth. That is not confusion. That is depth. What you need is not a single answer. It is something spacious enough to hold all of who you are. That exists.",
  "The Quiet Architect": "You carry more than one truth. That is not confusion. That is depth. What you need is not a single answer. It is something spacious enough to hold all of who you are. That exists.",
  "The Almost Arrived": "You carry more than one truth. That is not confusion. That is depth. What you need is not a single answer. It is something spacious enough to hold all of who you are. That exists."
};

const ThankYouScreen = ({ soulType }) => {
  const displayType = soulType && soulType.includes('&') ? 'The Woven Path' : soulType;
  const quote = CLOSING_QUOTES[displayType] || CLOSING_QUOTES["The Woven Path"];
  
  return (
    <div className="quiz-screen" data-testid="thank-you-screen">
      <div className="quiz-content" style={{ maxWidth: '40rem', margin: '0 auto' }}>
        <p 
          className="text-lg md:text-xl leading-[1.7] mb-12 animate-fade-in-up"
          style={{ color: 'var(--text-primary)' }}
        >
          Thank you for your honesty. It takes something to tell the truth about where you actually are. We will not waste that.
        </p>
        <div 
          className="animate-fade-in-up animation-delay-300"
          style={{ 
            borderTop: '1px solid var(--border)',
            paddingTop: '2.5rem',
            marginTop: '2rem'
          }}
        >
          <p 
            className="text-lg md:text-xl leading-[1.8] mb-6"
            style={{ 
              color: 'var(--text-primary)', 
              fontStyle: 'italic',
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 300
            }}
            data-testid="closing-quote"
          >
            &ldquo;{quote}&rdquo;
          </p>
          <p 
            className="text-sm md:text-base tracking-wide"
            style={{ color: 'var(--accent)', fontWeight: 500 }}
            data-testid="quote-attribution"
          >
            — Mz. Felicia Muhammad
          </p>
        </div>
      </div>
    </div>
  );
};

// Main Quiz Component
export default function Quiz() {
  const [currentScreen, setCurrentScreen] = useState("opening");
  const [answers, setAnswers] = useState({ love: null, health: null, money: null });
  const [result, setResult] = useState(null);
  const [responseId, setResponseId] = useState(null);
  
  // Track page view on mount
  useEffect(() => {
    axios.post(`${API}/page-view?view_type=quiz_start`).catch(() => {});
  }, []);
  
  const getProgress = useCallback(() => {
    const screens = ["opening", "love", "health", "money", "reveal", "email", "thankyou"];
    const index = screens.indexOf(currentScreen);
    return ((index) / (screens.length - 1)) * 100;
  }, [currentScreen]);
  
  const handleStart = () => {
    setCurrentScreen("love");
  };
  
  const handleAnswer = async (question, answer) => {
    const newAnswers = { ...answers, [question]: answer };
    setAnswers(newAnswers);
    
    // Move to next screen after selection
    setTimeout(async () => {
      if (question === "love") {
        setCurrentScreen("health");
      } else if (question === "health") {
        setCurrentScreen("money");
      } else if (question === "money") {
        // Submit quiz
        try {
          const response = await axios.post(`${API}/quiz`, {
            love_answer: newAnswers.love,
            health_answer: newAnswers.health,
            money_answer: answer
          });
          setResult(response.data);
          setResponseId(response.data.id);
          setCurrentScreen("reveal");
        } catch (err) {
          toast.error("Something went wrong. Please try again.");
        }
      }
    }, 400);
  };
  
  const handleEmailSubmit = () => {
    setCurrentScreen("thankyou");
  };
  
  const handleSkip = () => {
    setCurrentScreen("thankyou");
  };
  
  const handleContinueToEmail = () => {
    setCurrentScreen("email");
  };
  
  return (
    <div>
      {/* Progress Bar */}
      {currentScreen !== "opening" && (
        <div className="progress-bar" data-testid="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${getProgress()}%` }}
          />
        </div>
      )}
      
      {/* Screens */}
      {currentScreen === "opening" && (
        <OpeningScreen onStart={handleStart} />
      )}
      
      {currentScreen === "love" && (
        <QuestionScreen 
          question={QUESTIONS.love}
          selectedAnswer={answers.love}
          onSelect={(answer) => handleAnswer("love", answer)}
          step="love"
        />
      )}
      
      {currentScreen === "health" && (
        <QuestionScreen 
          question={QUESTIONS.health}
          selectedAnswer={answers.health}
          onSelect={(answer) => handleAnswer("health", answer)}
          step="health"
        />
      )}
      
      {currentScreen === "money" && (
        <QuestionScreen 
          question={QUESTIONS.money}
          selectedAnswer={answers.money}
          onSelect={(answer) => handleAnswer("money", answer)}
          step="money"
        />
      )}
      
      {currentScreen === "reveal" && result && (
        <RevealScreen 
          soulType={result.soul_type}
          onContinue={handleContinueToEmail}
        />
      )}
      
      {currentScreen === "email" && (
        <EmailScreen 
          responseId={responseId}
          onSubmit={handleEmailSubmit}
          onSkip={handleSkip}
        />
      )}
      
      {currentScreen === "thankyou" && (
        <ThankYouScreen soulType={result?.soul_type} />
      )}
    </div>
  );
}
